import { useContext, useEffect, useState } from "react";
import { Box, Chip, IconButton, LinearProgress, Sheet, Stack, Tooltip, Typography } from "@mui/joy";
import { AnimatePresence, motion } from "framer-motion";

import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import { LayoutContext } from "../../context";
import { APIGetManagerDashboardBrief } from "../../api";

const pctHex = (p) => (p >= 80 ? '#16a34a' : p >= 50 ? '#f59e0b' : '#dc2626');
const rub = (v) => `${Math.round(v || 0).toLocaleString('ru-RU')} ₽`;

const STORE_KEY = 'dashboard_brief_collapsed';

// Состояние «свёрнуто» переживает перезагрузку: кто свернул плашку один раз,
// не хочет видеть её снова на каждом заходе. localStorage может быть
// недоступен (приватный режим, запрет на данные сайта), поэтому и чтение,
// и запись — под try.
const readCollapsed = () => {
    try { return localStorage.getItem(STORE_KEY) === '1'; } catch (_) { return false; }
};
const writeCollapsed = (v) => {
    try { localStorage.setItem(STORE_KEY, v ? '1' : '0'); } catch (_) {}
};

/**
 * Компактная плашка «Мой результат за месяц» (главная / календарь).
 *
 * Была высокой колонкой: кольцо, два блока с прогрессом в полную ширину и
 * чипы — почти пол-экрана, и свернуть её было нельзя. Даже когда цель не
 * поставлена и показывать нечего, кроме нулей.
 *
 * Стало: одна строка-шапка, в которой всё главное видно сразу, и
 * сворачиваемое тело с прогрессом. Без цели тела нет вовсе — только строка.
 */
export default function ManagerDashboardBrief() {
    const { tokenEmployee, account } = useContext(LayoutContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(readCollapsed);

    useEffect(() => {
        if (!tokenEmployee) return;
        const isManager = Array.isArray(account?.position) &&
            (account.position.includes('manager') || account.position.includes('head_manager'));
        if (!isManager) return;

        setLoading(true);
        APIGetManagerDashboardBrief(tokenEmployee)
            .then(setData)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [tokenEmployee, account?.id]);

    if (!data) return null;

    const percent = data.overall_percent || 0;
    const hasGoal = data.has_goal;
    const bonus = data?.bonus_calculation?.total_salary;

    const eventsCount = data.events_count || 0;
    const completed = data.completed_events || 0;
    const revenue = data.revenue || 0;
    const avgCheck = completed > 0 ? revenue / completed : 0;
    const eventsProg = hasGoal && data.target_events_count
        ? (eventsCount / data.target_events_count) * 100 : 0;
    const isMarginGoal = data.metric_info?.metric === 'margin';
    const moneyActual = isMarginGoal ? data.margin || 0 : revenue;
    const moneyTarget = isMarginGoal ? data.target_margin : data.target_revenue;
    const revenueProg = hasGoal && moneyTarget ? moneyActual / moneyTarget * 100 : 0;

    const ringColor = pctHex(percent);
    const R = 20;                 // кольцо 48px вместо прежних 64
    const C = 2 * Math.PI * R;
    // Тело есть только при поставленной цели: без неё разворачивать нечего,
    // все цифры и так в шапке.
    const expandable = hasGoal;
    const open = expandable && !collapsed;

    const toggle = () => {
        const next = !collapsed;
        setCollapsed(next);
        writeCollapsed(next);
    };

    return (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
            <Sheet
                sx={{
                    borderRadius: '14px', border: '1px solid', borderColor: 'divider',
                    overflow: 'hidden', bgcolor: 'background.surface', mb: 1.5,
                    boxShadow: '0 2px 12px rgba(87,42,195,0.05)',
                }}
            >
                {/* ── Строка-шапка: всё главное видно, не разворачивая ── */}
                <Stack
                    direction="row" alignItems="center" spacing={1.5}
                    onClick={expandable ? toggle : undefined}
                    sx={{
                        px: { xs: 1.5, sm: 2 }, py: 1.25,
                        cursor: expandable ? 'pointer' : 'default',
                        background: 'linear-gradient(135deg, rgba(87,42,195,0.06), rgba(141,106,236,0.02))',
                        '&:hover': expandable ? { background: 'linear-gradient(135deg, rgba(87,42,195,0.10), rgba(141,106,236,0.04))' } : null,
                        transition: 'background .15s',
                    }}
                >
                    {hasGoal ? (
                        <Box sx={{ position: 'relative', width: 48, height: 48, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width={48} height={48} viewBox="0 0 48 48" style={{ position: 'absolute' }}>
                                <circle cx="24" cy="24" r={R} fill="none" stroke="#fff" strokeWidth="5" opacity="0.8" />
                                <circle cx="24" cy="24" r={R} fill="none" stroke={ringColor} strokeWidth="5" strokeLinecap="round"
                                    strokeDasharray={C}
                                    strokeDashoffset={C * (1 - Math.min(percent, 100) / 100)}
                                    transform="rotate(-90 24 24)" />
                            </svg>
                            <Typography level="body-xs" sx={{ fontWeight: 800, color: ringColor, fontSize: 11.5 }}>
                                {Math.round(percent)}%
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'neutral.softBg', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FlagRoundedIcon sx={{ fontSize: 18, color: 'text.tertiary', opacity: 0.55 }} />
                        </Box>
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography level="title-sm" sx={{ fontWeight: 800, lineHeight: 1.25 }}>
                            Мой результат за месяц
                        </Typography>
                        {/* Цифры прямо в шапке — свёрнутая плашка всё равно
                            отвечает на вопрос «как у меня дела». */}
                        <Stack direction="row" spacing={1.25} sx={{ mt: 0.25, flexWrap: 'wrap', rowGap: 0.25 }}>
                            <Metric icon={<EventNoteRoundedIcon sx={{ fontSize: 13 }} />}
                                value={hasGoal && data.target_events_count
                                    ? `${eventsCount} / ${data.target_events_count}`
                                    : String(eventsCount)}
                                title={`Событий за месяц${completed ? `, завершено ${completed}` : ''}`} />
                            <Metric icon={<PaidRoundedIcon sx={{ fontSize: 13 }} />}
                                value={rub(revenue)} title="Выручка за месяц" />
                            {completed > 0 && (
                                <Metric value={`ср. ${rub(avgCheck)}`} title="Средний чек" />
                            )}
                            {!hasGoal && (
                                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                    цель не поставлена
                                </Typography>
                            )}
                        </Stack>
                    </Box>

                    {hasGoal && bonus > 0 && (
                        <Tooltip title="Расчётная выплата за месяц" variant="soft" size="sm">
                            <Chip size="sm" variant="soft" color="success"
                                startDecorator={<EmojiEventsRoundedIcon sx={{ fontSize: 13 }} />}
                                sx={{ fontWeight: 700, flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}>
                                ≈ {rub(bonus)}
                            </Chip>
                        </Tooltip>
                    )}

                    {expandable && (
                        <IconButton
                            size="sm" variant="plain" color="neutral"
                            onClick={(e) => { e.stopPropagation(); toggle(); }}
                            title={open ? 'Свернуть' : 'Развернуть'}
                            sx={{
                                flexShrink: 0, transition: 'transform .18s',
                                transform: open ? 'rotate(180deg)' : 'none',
                            }}
                        >
                            <KeyboardArrowDownRoundedIcon />
                        </IconButton>
                    )}
                </Stack>

                {/* ── Тело: прогресс по цели ── */}
                <AnimatePresence initial={false}>
                    {open && (
                        <Box component={motion.div}
                            key="body"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2, ease: 'easeInOut' }}
                            sx={{ overflow: 'hidden' }}
                        >
                            <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.25, pb: 1.5 }}>
                                <GoalRow
                                    label="События" color="#6d28d9"
                                    value={`${eventsCount}${data.target_events_count ? ` / ${data.target_events_count}` : ''}`}
                                    hint={completed ? `завершено: ${completed}` : null}
                                    progress={eventsProg}
                                />
                                <Box sx={{ mt: 1.25 }}>
                                    <GoalRow
                                        label={isMarginGoal ? "Маржа" : "Выручка"} color="#f59e0b"
                                        value={`${rub(moneyActual)}${moneyTarget ? ` / ${rub(moneyTarget)}` : ''}`}
                                        progress={revenueProg}
                                    />
                                </Box>

                                {(data.metric_info?.metric_label || (bonus > 0)) && (
                                    <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center' }}>
                                        {data.metric_info?.metric_label && (
                                            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                                Считается по: {data.metric_info.metric_label}
                                            </Typography>
                                        )}
                                        {bonus > 0 && (
                                            <Chip size="sm" variant="soft" color="success"
                                                startDecorator={<EmojiEventsRoundedIcon sx={{ fontSize: 13 }} />}
                                                sx={{ fontWeight: 700, display: { xs: 'inline-flex', sm: 'none' } }}>
                                                ≈ {rub(bonus)}
                                            </Chip>
                                        )}
                                    </Stack>
                                )}
                            </Box>
                        </Box>
                    )}
                </AnimatePresence>

                {loading && <LinearProgress color="primary" sx={{ '--LinearProgress-thickness': '2px' }} />}
            </Sheet>
        </motion.div>
    );
}


function Metric({ icon, value, title }) {
    return (
        <Tooltip title={title || ''} variant="soft" size="sm">
            <Stack direction="row" spacing={0.4} sx={{ alignItems: 'center', color: 'text.secondary' }}>
                {icon}
                <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}>
                    {value}
                </Typography>
            </Stack>
        </Tooltip>
    );
}


function GoalRow({ label, value, hint, progress, color }) {
    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.4, gap: 1 }}>
                <Typography level="body-xs" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                    {label}
                    {hint && (
                        <Typography component="span" level="body-xs" sx={{ color: 'text.tertiary', fontWeight: 400, ml: 0.75 }}>
                            {hint}
                        </Typography>
                    )}
                </Typography>
                <Typography level="body-xs" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>
                    {value}
                </Typography>
            </Stack>
            <LinearProgress determinate value={Math.max(0, Math.min(progress || 0, 100))} thickness={5}
                sx={{ borderRadius: '99px', '--LinearProgress-progressColor': color }} />
        </Box>
    );
}
